import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { battlePersonApi } from '../../api/battlePerson';
import { UserContext } from '../../context/UserContext';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import '../../css/components/JoinBattleModal.css';

export default function JoinBattleModal({ isOpen, onClose, battleId, onSuccess }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        nickname: '',
        city: '',
        seed: '',
    });

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        if (!formData.name || !formData.name.trim()) {
            setError('Введите имя');
            return false;
        }
        if (!formData.nickname || !formData.nickname.trim()) {
            setError('Введите никнейм');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        // Проверка: есть ли у пользователя профиль
        const publicProfileId = currentUser?.publicProfile?.id ||
            (Array.isArray(currentUser?.profiles) && currentUser.profiles[0]?.id) ||
            null;

        if (!publicProfileId) {
            setError('У вас нет созданного профиля. Пожалуйста, создайте профиль в личном кабинете.');
            return;
        }

        if (!battleId) {
            setError('ID баттла не указан');
            return;
        }

        try {
            setLoading(true);

            // Формируем payload согласно API: POST /api/battle_person
            const payload = {
                battleId: battleId,
                publicProfile: publicProfileId,
                name: formData.name.trim(),
                nickName: formData.nickname.trim(),
                city: formData.city.trim() || null,
                role: 'PARTICIPANT',
                seed: formData.seed ? parseInt(formData.seed) : null,
            };

            await battlePersonApi.create(payload);

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess();
                }
                handleClose();
            }, 1500);
        } catch (err) {
            console.error('[JoinBattleModal] Failed to join battle:', err.message);
            setError(err.response?.data?.message || err.message || 'Не удалось записаться на баттл');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            nickname: '',
            city: '',
            seed: '',
        });
        setError(null);
        setSuccess(false);
        if (onClose) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <HrnbModal open={isOpen} onClose={handleClose} title="Регистрация на баттл" size="medium">
            <div className="join-battle-modal">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {success && (
                    <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                        Вы успешно записались на баттл!
                    </HrnbAlert>
                )}

                <form onSubmit={handleSubmit} className="join-battle-form" autoComplete="off">
                    <div className="form-row">
                        <HrnbInput
                            label="Имя *"
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', typeof e === 'string' ? e : e.target?.value)}
                            placeholder="Введите ваше имя"
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <HrnbInput
                            label="Никнейм *"
                            type="text"
                            value={formData.nickname}
                            onChange={(e) => handleChange('nickname', typeof e === 'string' ? e : e.target?.value)}
                            placeholder="Введите ваш никнейм"
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-row">
                        <HrnbInput
                            label="Город"
                            type="text"
                            value={formData.city}
                            onChange={(e) => {
                                const value = typeof e === 'string' ? e : e.target?.value;
                                handleChange('city', value);
                            }}
                            placeholder="Ваш город"
                            disabled={loading}
                        />
                    </div>

                    <div className="form-row">
                        <HrnbInput
                            label="Посев (необязательно)"
                            type="number"
                            value={formData.seed}
                            onChange={(e) => {
                                const value = typeof e === 'string' ? e : e.target?.value;
                                handleChange('seed', value);
                            }}
                            placeholder="Например: 1"
                            min="1"
                            disabled={loading}
                            hint="Позиция в турнирной сетке (если применимо)"
                        />
                    </div>

                    <div className="join-battle-modal__info">
                        <p className="text-muted">
                            После отправки заявки она будет отправлена на модерацию организаторами баттла.
                        </p>
                    </div>

                    <div className="form-actions">
                        <HrnbButton variant="outline" onClick={handleClose} disabled={loading}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton type="submit" variant="primary" disabled={loading}>
                            {loading ? <HrnbLoader size="sm" /> : 'Записаться'}
                        </HrnbButton>
                    </div>
                </form>
            </div>
        </HrnbModal>
    );
}
